"use client";

import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import API from "../utils/api";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

const StockReport = () => {
  // Default to "All" as requested
  const [activeTab, setActiveTab] = useState("all");
  const [fromDate, setFromDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [toDate, setToDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [allTimeData, setAllTimeData] = useState({ purchases: [], adjustments: [], returns: [], sales: [] });

  // Keep a valid date range at all times
  useEffect(() => {
    try {
      const f = new Date(`${fromDate}T00:00:00`).getTime();
      const t = new Date(`${toDate}T23:59:59`).getTime();
      if (Number.isFinite(f) && Number.isFinite(t) && f > t) {
        setToDate(fromDate);
      }
    } catch (_) {}
  }, [fromDate]);
  useEffect(() => {
    try {
      const f = new Date(`${fromDate}T00:00:00`).getTime();
      const t = new Date(`${toDate}T23:59:59`).getTime();
      if (Number.isFinite(f) && Number.isFinite(t) && t < f) {
        setFromDate(toDate);
      }
    } catch (_) {}
  }, [toDate]);

  const displayRows = useMemo(() => {
    // Show all products; do not hide zeros
    return Array.isArray(rows) ? rows : [];
  }, [rows]);

  const filteredRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return displayRows;
    return displayRows.filter((r) =>
      [r?.companyName, r?.modelNo, r?.size]
        .map((v) => String(v || "").toLowerCase())
        .some((v) => v.includes(q))
    );
  }, [displayRows, searchQuery]);

  const sortedRows = useMemo(() => {
    const toTime = (val) => {
      if (!val) return 0;
      const t = new Date(val).getTime();
      return Number.isFinite(t) ? t : 0;
    };

    const tryArray = (val) => (Array.isArray(val) ? val : undefined);

    const parseQty = (val) => {
      const n = Number(val);
      return Number.isFinite(n) ? n : 0;
    };

    const getSaleQty = (r) => {
      const numericFields = [
        r?.saleQuantity,
        r?.soldQuantity,
        r?.totalSold,
        r?.todaySales,
        r?.quantitySold,
        r?.salesQty,
        r?.todaySaleQty,
      ];
      const found = numericFields.find((v) => typeof v !== "undefined");
      if (typeof found !== "undefined") return parseQty(found);
      const arrays =
        tryArray(r?.sales) ||
        tryArray(r?.salesHistory) ||
        tryArray(r?.transactions);
      if (arrays) {
        const start = new Date(`${fromDate}T00:00:00`);
        const end = new Date(`${toDate}T23:59:59`);
        const filterByDate = (arr) => {
          if (activeTab === "today") {
            const todayStr = format(new Date(), "yyyy-MM-dd");
            return arr.filter((x) => {
              const d = new Date(x?.date || x?.createdAt || x?.invoiceDate);
              return format(d, "yyyy-MM-dd") === todayStr;
            });
          }
          if (activeTab === "range") {
            return arr.filter((x) => {
              const d = new Date(x?.date || x?.createdAt || x?.invoiceDate);
              return d >= start && d <= end;
            });
          }
          return arr;
        };
        return filterByDate(arrays).reduce(
          (sum, it) => sum + parseQty(it?.quantity || it?.qty || 0),
          0
        );
      }
      return 0;
    };

    const getPurchaseQtyLocal = (r) => {
      const candidates = [
        r?.purchaseQuantity,
        r?.purchasedQuantity,
        r?.totalPurchased,
        r?.todayPurchases,
        r?.quantityPurchased,
        r?.purchase,
        r?.totalPurchase,
        r?.purchaseQty,
        r?.purchasesQty,
        r?.purchasedQty,
      ];
      const value = candidates.find((v) => typeof v !== "undefined");
      if (typeof value !== "undefined") return parseQty(value);

      const arrays =
        tryArray(r?.purchases) ||
        tryArray(r?.purchaseHistory) ||
        tryArray(r?.purchaseDetails) ||
        tryArray(r?.purchaseRecords);

      if (arrays) {
        const start = new Date(`${fromDate}T00:00:00`);
        const end = new Date(`${toDate}T23:59:59`);
        const filterByDate = (arr) => {
          if (activeTab === "today") {
            const todayStr = format(new Date(), "yyyy-MM-dd");
            return arr.filter((x) => {
              const d = new Date(x?.date || x?.createdAt || x?.invoiceDate);
              return format(d, "yyyy-MM-dd") === todayStr;
            });
          }
          if (activeTab === "range") {
            return arr.filter((x) => {
              const d = new Date(x?.date || x?.createdAt || x?.invoiceDate);
              return d >= start && d <= end;
            });
          }
          return arr;
        };
        return filterByDate(arrays).reduce(
          (sum, it) => sum + parseQty(it?.quantity || it?.qty || 0),
          0
        );
      }
      return 0;
    };

    const getLastActivity = (r) => {
      const times = [
        r?.lastActivityAt,
        r?.updatedAt,
        r?.lastSaleAt,
        r?.lastPurchaseAt,
        r?.invoiceDate,
        r?.createdAt,
      ]
        .map((v) => toTime(v))
        .filter((n) => Number.isFinite(n) && n > 0);

      const collectDates = (arr) =>
        Array.isArray(arr)
          ? arr
              .map((x) => x?.date || x?.createdAt || x?.invoiceDate)
              .map((d) => toTime(d))
              .filter((n) => Number.isFinite(n) && n > 0)
          : [];

      times.push(
        ...collectDates(r?.sales),
        ...collectDates(r?.salesHistory),
        ...collectDates(r?.transactions),
        ...collectDates(r?.purchases),
        ...collectDates(r?.purchaseHistory),
        ...collectDates(r?.purchaseDetails)
      );
      return times.length > 0 ? Math.max(...times) : 0;
    };

    const copy = [...filteredRows];
    copy.sort((a, b) => {
      const aActivity = (getPurchaseQtyLocal(a) + getSaleQty(a)) > 0 ? 1 : 0;
      const bActivity = (getPurchaseQtyLocal(b) + getSaleQty(b)) > 0 ? 1 : 0;
      if (aActivity !== bActivity) return bActivity - aActivity; // Transacted first

      const aLast = getLastActivity(a);
      const bLast = getLastActivity(b);
      if (aLast !== bLast) return bLast - aLast; // latest first

      const aName = `${a?.companyName || ""} ${a?.modelNo || ""}`.trim();
      const bName = `${b?.companyName || ""} ${b?.modelNo || ""}`.trim();
      return aName.localeCompare(bName);
    });
    return copy;
  }, [filteredRows, activeTab, fromDate, toDate]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(sortedRows.length / itemsPerPage)), [sortedRows.length, itemsPerPage]);
  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return sortedRows.slice(start, start + itemsPerPage);
  }, [sortedRows, currentPage, itemsPerPage]);

  const queryString = useMemo(() => {
    // Build params for backend. For 'all' we pass no params
    const params = new URLSearchParams();
    if (activeTab === "today") {
      params.set("period", "today");
    } else if (activeTab === "range" && fromDate && toDate) {
      params.set("start", fromDate);
      params.set("end", toDate);
    }
    const qs = params.toString();
    return qs;
  }, [activeTab, fromDate, toDate]);

  const fetchData = async () => {
    setLoading(true);
    setError("");
    try {
      const url = queryString
        ? `/products/stock/report?${queryString}`
        : `/products/stock/report`;

      // Build purchases API URL based on the active tab and date range
      const buildPurchasesUrl = () => {
        if (activeTab === "today") {
          const today = format(new Date(), "yyyy-MM-dd");
          return `/purchases/report?start=${today}&end=${today}`;
        }
        if (activeTab === "range" && fromDate && toDate) {
          return `/purchases/report?start=${fromDate}&end=${toDate}`;
        }
        // All-time purchases
        return `/purchases/report`;
      };

      // Build adjustments API URL similar to purchases
      const buildAdjustmentsUrl = () => {
        if (activeTab === "today") {
          const today = format(new Date(), "yyyy-MM-dd");
          return `/products/adjustments/report?startDate=${today}&endDate=${today}`;
        }
        if (activeTab === "range" && fromDate && toDate) {
          return `/products/adjustments/report?startDate=${fromDate}&endDate=${toDate}`;
        }
        // All-time adjustments
        return `/products/adjustments/report`;
      };

      // Build returns API URL similar to purchases (sale returns)
      const buildReturnsUrl = () => {
        if (activeTab === "today") {
          const today = format(new Date(), "yyyy-MM-dd");
          return `/returns/report?start=${today}&end=${today}`;
        }
        if (activeTab === "range" && fromDate && toDate) {
          return `/returns/report?start=${fromDate}&end=${toDate}`;
        }
        // All-time returns
        return `/returns/report`;
      };

      const purchasesUrl = buildPurchasesUrl();
      const adjustmentsUrl = buildAdjustmentsUrl();
      const returnsUrl = buildReturnsUrl();

      // Fetch all-time data for date-based remaining calculation
      const [allTimePurchasesRes, allTimeAdjustmentsRes, allTimeReturnsRes, allTimeSalesRes] = await Promise.all([
        API.get('/purchases/report').catch(() => ({ data: [] })),
        API.get('/products/adjustments/report').catch(() => ({ data: {} })),
        API.get('/returns/report').catch(() => ({ data: [] })),
        API.get('/products/stock/report').catch(() => ({ data: [] })),
      ]);

      // Store all-time data for remaining calculation
      const allTimePurchases = Array.isArray(allTimePurchasesRes.data)
        ? allTimePurchasesRes.data
        : (Array.isArray(allTimePurchasesRes.data?.rows)
            ? allTimePurchasesRes.data.rows
            : (Array.isArray(allTimePurchasesRes.data?.purchases) ? allTimePurchasesRes.data.purchases : []));
      
      const allTimeAdjustments = Array.isArray(allTimeAdjustmentsRes.data?.adjustments)
        ? allTimeAdjustmentsRes.data.adjustments
        : (Array.isArray(allTimeAdjustmentsRes.data) ? allTimeAdjustmentsRes.data : []);
      
      const allTimeReturns = Array.isArray(allTimeReturnsRes.data?.returns)
        ? allTimeReturnsRes.data.returns
        : (Array.isArray(allTimeReturnsRes.data?.rows)
            ? allTimeReturnsRes.data.rows
            : (Array.isArray(allTimeReturnsRes.data) ? allTimeReturnsRes.data : []));

      const allTimeStockData = Array.isArray(allTimeSalesRes.data)
        ? allTimeSalesRes.data
        : (Array.isArray(allTimeSalesRes.data?.items) ? allTimeSalesRes.data.items : []);

      setAllTimeData({
        purchases: allTimePurchases,
        adjustments: allTimeAdjustments,
        returns: allTimeReturns,
        sales: allTimeStockData,
      });

      // Fetch stock report, purchases, adjustments, and returns in parallel
      const [stockRes, purchasesRes, adjustmentsRes, returnsRes] = await Promise.all([
        API.get(url),
        API.get(purchasesUrl).catch(() => ({ data: [] })),
        API.get(adjustmentsUrl).catch(() => ({ data: {} })),
        API.get(returnsUrl).catch(() => ({ data: [] })),
      ]);

      const stockData = Array.isArray(stockRes.data)
        ? stockRes.data
        : (Array.isArray(stockRes.data?.items) ? stockRes.data.items : []);

      const purchasesRaw = Array.isArray(purchasesRes.data)
        ? purchasesRes.data
        : (Array.isArray(purchasesRes.data?.rows)
            ? purchasesRes.data.rows
            : (Array.isArray(purchasesRes.data?.purchases) ? purchasesRes.data.purchases : []));

      const adjustmentsRaw = Array.isArray(adjustmentsRes.data?.adjustments)
        ? adjustmentsRes.data.adjustments
        : (Array.isArray(adjustmentsRes.data) ? adjustmentsRes.data : []);

      const returnsRaw = Array.isArray(returnsRes.data?.returns)
        ? returnsRes.data.returns
        : (Array.isArray(returnsRes.data?.rows)
            ? returnsRes.data.rows
            : (Array.isArray(returnsRes.data) ? returnsRes.data : []));

      // Normalize returns for safer aggregation (ensure size, productId, quantity)
      const normalizeReturn = (r) => {
        const productId = r.productId || (typeof r.product === 'string' ? r.product : r.product?._id);
        const companyName = r.companyName || r.product?.companyName || '';
        const modelNo = r.modelNo || r.product?.modelNo || '';
        const size = r.size || r.product?.size || r.productSize || '';
        // Prefer explicit numeric quantity
        const qty = Number(r.quantity);
        return {
          productId,
          companyName,
          modelNo,
          size,
          quantity: Number.isFinite(qty) ? qty : 0,
        };
      };
      const returns = returnsRaw.map(normalizeReturn);

      // Helper to create a stable key for matching
      const makeKey = (company, model, size) =>
        [company || "", model || "", size || ""]
          .map((s) => String(s).trim().toLowerCase())
          .join("||");

      // Aggregate purchase quantities by (companyName, modelNo, size)
      const purchaseSums = purchasesRaw.reduce((acc, p) => {
        const company = p.companyName || p.product?.companyName || "";
        const model = p.modelNo || p.product?.modelNo || "";
        const size = p.size || p.product?.size || "";
        const key = makeKey(company, model, size);
        const qty = Number(p?.quantity) || 0;
        acc[key] = (acc[key] || 0) + qty;
        return acc;
      }, {});

      // Aggregate adjustment increase/decrease by (companyName, modelNo, size)
      const adjustmentSums = adjustmentsRaw.reduce((acc, a) => {
        const company = a.companyName || a.product?.companyName || "";
        const model = a.modelNo || a.product?.modelNo || "";
        const size = a.size || a.product?.size || "";
        const key = makeKey(company, model, size);
        const qty = Number(a?.quantity) || 0;
        const action = String(a?.action || "").toLowerCase();
        if (!acc[key]) acc[key] = { inc: 0, dec: 0 };
        if (action === "increase") acc[key].inc += qty;
        else if (action === "decrease") acc[key].dec += qty;
        return acc;
      }, {});

      // Aggregate sale return quantities by (companyName, modelNo, size) and by productId as fallback
      const returnSumsByKey = returns.reduce((acc, r) => {
        const key = makeKey(r.companyName, r.modelNo, r.size);
        acc[key] = (acc[key] || 0) + (Number(r.quantity) || 0);
        return acc;
      }, {});
      const returnSumsById = returns.reduce((acc, r) => {
        if (!r.productId) return acc;
        acc[r.productId] = (acc[r.productId] || 0) + (Number(r.quantity) || 0);
        return acc;
      }, {});

      // Merge purchase totals and sale returns into stock rows
      const merged = stockData.map((r) => {
        const key = makeKey(r?.companyName, r?.modelNo, r?.size);
        const mergedRow = { ...r };
        // Prefer explicit numeric field so UI uses it directly
        mergedRow.purchaseQuantity = purchaseSums[key] || 0;
        const adj = adjustmentSums[key] || { inc: 0, dec: 0 };
        mergedRow.adjustIncQty = adj.inc;
        mergedRow.adjustDecQty = adj.dec;
        // Try key match; if 0, fallback to productId-based sum if available
        const byKey = returnSumsByKey[key] || 0;
        const byId = (r?._id && returnSumsById[r._id]) || 0;
        mergedRow.saleReturnQty = byKey || byId || 0;
        return mergedRow;
      });

      setRows(merged);
    } catch (e) {
      setRows([]);
      setError("Failed to load stock report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryString]);

  const renderProductName = (r) => {
    const company = typeof r.companyName === "string" ? r.companyName.trim() : "";
    const model = typeof r.modelNo === "string" ? r.modelNo.trim() : "";
    const name = [company, model].filter(Boolean).join(" ");
    return name || "-";
  };

  const getPurchaseQty = (r) => {
    const parseQty = (val) => {
      const n = Number(val);
      return Number.isFinite(n) ? n : 0;
    };
    const numericCandidates = [
      r?.purchaseQuantity,
      r?.purchasedQuantity,
      r?.totalPurchased,
      r?.todayPurchases,
      r?.quantityPurchased,
      r?.purchase,
      r?.totalPurchase,
      r?.purchaseQty,
      r?.purchasesQty,
      r?.purchasedQty,
      // Additional common backend field names
      r?.totalPurchaseQty,
      r?.totalPurchases,
      r?.purchaseCount,
      r?.purchasesCount,
      r?.purchaseTotal,
      r?.stockIn,
      r?.stockInQty,
      r?.stockInQuantity,
      r?.inQuantity,
      r?.inQty,
      r?.quantityIn,
      r?.qtyIn,
    ];
    const value = numericCandidates.find((v) => typeof v !== "undefined");
    if (typeof value !== "undefined") return parseQty(value);

    const arrayCandidate = Array.isArray(r?.purchases)
      ? r?.purchases
      : Array.isArray(r?.purchaseHistory)
      ? r?.purchaseHistory
      : Array.isArray(r?.purchaseDetails)
      ? r?.purchaseDetails
      : Array.isArray(r?.purchaseRecords)
      ? r?.purchaseRecords
      : Array.isArray(r?.inflows)
      ? r?.inflows
      : Array.isArray(r?.stockIn)
      ? r?.stockIn
      : Array.isArray(r?.stockIns)
      ? r?.stockIns
      : Array.isArray(r?.inTransactions)
      ? r?.inTransactions
      : undefined;

    if (arrayCandidate) {
      const items = (() => {
        if (activeTab === "today") {
          const todayStr = format(new Date(), "yyyy-MM-dd");
          return arrayCandidate.filter((x) => {
            const d = new Date(x?.date || x?.createdAt || x?.invoiceDate);
            return format(d, "yyyy-MM-dd") === todayStr;
          });
        }
        if (activeTab === "range" && fromDate && toDate) {
          const start = new Date(`${fromDate}T00:00:00`);
          const end = new Date(`${toDate}T23:59:59`);
          return arrayCandidate.filter((x) => {
            const d = new Date(x?.date || x?.createdAt || x?.invoiceDate);
            return d >= start && d <= end;
          });
        }
        return arrayCandidate;
      })();
      return items.reduce((sum, it) => sum + parseQty(it?.quantity || it?.qty || 0), 0);
    }
    // As a final, safe fallback for "All" view, derive purchases from sale + remaining
    if (activeTab === "all") {
      const remaining = Number(r?.remainingQuantity ?? r?.quantity ?? 0);
      const sales = getSaleQtyForDisplay(r);
      const approx = parseQty(remaining) + parseQty(sales);
      return approx;
    }
    return 0;
  };

  // Compute sale quantity with date awareness and multiple field compatibility
  const getSaleQtyForDisplay = (r) => {
    const parseQty = (val) => {
      const n = Number(val);
      return Number.isFinite(n) ? n : 0;
    };

    // Prefer array-based computation when filtering by date
    const salesArray = Array.isArray(r?.sales)
      ? r?.sales
      : Array.isArray(r?.salesHistory)
      ? r?.salesHistory
      : Array.isArray(r?.transactions)
      ? r?.transactions
      : undefined;

    if (salesArray) {
      const items = (() => {
        if (activeTab === "today") {
          const todayStr = format(new Date(), "yyyy-MM-dd");
          return salesArray.filter((x) => {
            const d = new Date(x?.date || x?.createdAt || x?.invoiceDate);
            return format(d, "yyyy-MM-dd") === todayStr;
          });
        }
        if (activeTab === "range" && fromDate && toDate) {
          const start = new Date(`${fromDate}T00:00:00`);
          const end = new Date(`${toDate}T23:59:59`);
          return salesArray.filter((x) => {
            const d = new Date(x?.date || x?.createdAt || x?.invoiceDate);
            return d >= start && d <= end;
          });
        }
        return salesArray;
      })();
      return items.reduce((sum, it) => sum + parseQty(it?.quantity || it?.qty || 0), 0);
    }

    // Fallbacks when only numeric fields exist
    if (activeTab === "today") {
      const todayCandidates = [r?.todaySaleQty, r?.todaySales];
      const found = todayCandidates.find((v) => typeof v !== "undefined");
      if (typeof found !== "undefined") return parseQty(found);
      // As last resort, nothing specific for today
      return 0;
    }
    if (activeTab === "range") {
      // Without arrays we cannot derive range accurately
      return 0;
    }
    // All-time totals
    const allTime = [
      r?.saleQuantity,
      r?.soldQuantity,
      r?.totalSold,
      r?.quantitySold,
      r?.salesQty,
    ];
    const any = allTime.find((v) => typeof v !== "undefined");
    return typeof any !== "undefined" ? parseQty(any) : 0;
  };

  // Calculate remaining quantity at the selected date
  const getRemainingQty = (r) => {
    // For "all" tab, show current remaining
    if (activeTab === "all") {
      return Number(r.remainingQuantity ?? r.quantity ?? 0);
    }

    // If allTimeData is not loaded yet, show current remaining as fallback
    if (!allTimeData || !Array.isArray(allTimeData.purchases)) {
      return Number(r.remainingQuantity ?? r.quantity ?? 0);
    }

    // For "today" or "range" tabs, calculate remaining at that date
    const endDate = activeTab === "today" 
      ? new Date(format(new Date(), "yyyy-MM-dd") + "T23:59:59")
      : new Date(`${toDate}T23:59:59`);

    const makeKey = (company, model, size) =>
      [company || "", model || "", size || ""]
        .map((s) => String(s).trim().toLowerCase())
        .join("||");

    const key = makeKey(r?.companyName, r?.modelNo, r?.size);
    const currentRemaining = Number(r.remainingQuantity ?? r.quantity ?? 0);

    // Calculate transactions after the end date
    let purchasesAfter = 0;
    let salesAfter = 0;
    let adjIncAfter = 0;
    let adjDecAfter = 0;
    let returnsAfter = 0;

    // Process all-time purchases
    allTimeData.purchases.forEach((p) => {
      const pCompany = p.companyName || p.product?.companyName || "";
      const pModel = p.modelNo || p.product?.modelNo || "";
      const pSize = p.size || p.product?.size || "";
      const pKey = makeKey(pCompany, pModel, pSize);
      
      if (pKey === key) {
        const pDate = new Date(p.purchaseDate || p.date || p.createdAt);
        if (pDate > endDate) {
          purchasesAfter += Number(p.quantity || 0);
        }
      }
    });

    // Process all-time sales (from stock data - find matching product in allTimeStockData)
    const matchingStock = allTimeData.sales.find((s) => {
      const sKey = makeKey(s?.companyName, s?.modelNo, s?.size);
      return sKey === key;
    });
    
    if (matchingStock && Array.isArray(matchingStock.sales)) {
      matchingStock.sales.forEach((s) => {
        const sDate = new Date(s.date || s.saleDate || s.createdAt);
        if (sDate > endDate) {
          salesAfter += Number(s.quantity || 0);
        }
      });
    }

    // Process all-time adjustments
    allTimeData.adjustments.forEach((a) => {
      const aCompany = a.companyName || a.product?.companyName || "";
      const aModel = a.modelNo || a.product?.modelNo || "";
      const aSize = a.size || a.product?.size || "";
      const aKey = makeKey(aCompany, aModel, aSize);
      
      if (aKey === key) {
        const aDate = new Date(a.date || a.createdAt);
        if (aDate > endDate) {
          const qty = Number(a.quantity || 0);
          const action = String(a.action || "").toLowerCase();
          if (action === "increase") {
            adjIncAfter += qty;
          } else if (action === "decrease") {
            adjDecAfter += qty;
          }
        }
      }
    });

    // Process all-time returns
    allTimeData.returns.forEach((ret) => {
      const retCompany = ret.companyName || ret.product?.companyName || "";
      const retModel = ret.modelNo || ret.product?.modelNo || "";
      const retSize = ret.size || ret.product?.size || "";
      const retKey = makeKey(retCompany, retModel, retSize);
      
      if (retKey === key) {
        const retDate = new Date(ret.date || ret.returnDate || ret.createdAt);
        if (retDate > endDate) {
          returnsAfter += Number(ret.quantity || 0);
        }
      }
    });

    // Remaining at date = Current Remaining - (Transactions after endDate)
    // Transactions after = Purchases - Sales + Adj+ - Adj- - Returns
    const transactionsAfter = purchasesAfter - salesAfter + adjIncAfter - adjDecAfter - returnsAfter;
    const remainingAtDate = currentRemaining - transactionsAfter;

    return remainingAtDate;
  };

  const exportToExcel = () => {
    const data = sortedRows.map((r, idx) => ({
      "No.": idx + 1,
      Company: r.companyName || "-",
      "Product Name": r.modelNo || "-",
      Size: r.size || "-",
      Purchase: getPurchaseQty(r),
      "Adj Increase": Number(r.adjustIncQty || 0),
      "Adj Decrease": Number(r.adjustDecQty || 0),
      "Sale Return": Number(r.saleReturnQty || 0),
      // Use computed sale quantity that also considers arrays and date filters
      Sale: getSaleQtyForDisplay(r),
      "Remaining Qty": getRemainingQty(r),
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Stock");
    XLSX.writeFile(wb, `stock_${format(new Date(), "yyyy-MM-dd")}.xlsx`);
  };

  const exportToPDF = () => {
    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text("Stock Report", 105, 14, { align: "center" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    const subtitle =
      activeTab === "today"
        ? `Today — ${format(new Date(), "yyyy-MM-dd")}`
        : activeTab === "range"
        ? `From ${fromDate} to ${toDate}`
        : "All";
    doc.text(subtitle, 105, 20, { align: "center" });

    const body = sortedRows.map((r) => [
      r.companyName || "-",
      r.modelNo || "-",
      r.size || "-",
      String(getPurchaseQty(r)),
      String(Number(r.adjustIncQty || 0)),
      String(Number(r.adjustDecQty || 0)),
      String(Number(r.saleReturnQty || 0)),
      String(getSaleQtyForDisplay(r)),
      String(getRemainingQty(r)),
    ]);

    autoTable(doc, {
      head: [["Company", "Product", "Size", "Purchase", "Adj +", "Adj -", "Sale Return", "Sale", "Remaining"]],
      body,
      startY: 26,
      styles: { fontSize: 9 },
      headStyles: { fillColor: [41, 128, 185] },
      columnStyles: { 3: { halign: "right" }, 4: { halign: "right" }, 5: { halign: "right" }, 6: { halign: "right" }, 7: { halign: "right" }, 8: { halign: "right" } },
    });

    doc.save(`stock_${format(new Date(), "yyyy-MM-dd")}.pdf`);
  };

  return (
    <div className="sales-reports-container" style={{ padding: 16 }}>
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <h2>Stock</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="export-button" onClick={exportToExcel} disabled={filteredRows.length === 0}>Export Excel</button>
          <button className="export-button" onClick={exportToPDF} disabled={filteredRows.length === 0}>Export PDF</button>
        </div>
      </div>

      <div className="tabs" style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <button
          className={`tab-btn ${activeTab === "all" ? "active" : ""}`}
          onClick={() => setActiveTab("all")}
        >
          All
        </button>
        <button
          className={`tab-btn ${activeTab === "today" ? "active" : ""}`}
          onClick={() => setActiveTab("today")}
        >
          Today
        </button>
        <button
          className={`tab-btn ${activeTab === "range" ? "active" : ""}`}
          onClick={() => setActiveTab("range")}
        >
          From–To
        </button>
      </div>

      {activeTab === "range" && (
        <div className="range-controls" style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          <span>to</span>
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
          <button className="apply-btn" onClick={fetchData}>Apply</button>
          <span style={{ color: '#666', fontSize: 12 }}>
            Showing {format(new Date(fromDate), 'dd MMM yyyy')} → {format(new Date(toDate), 'dd MMM yyyy')}
          </span>
        </div>
      )}

      <div className="search-container" style={{ marginBottom: 12 }}>
        <input
          type="text"
          placeholder="Search by company, product or size..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="search-input"
          style={{ width: '100%', maxWidth: 420, padding: 8 }}
        />
      </div>

      {loading ? (
        <div className="loading">Loading...</div>
      ) : sortedRows.length === 0 ? (
        <p className="no-data">No records found.</p>
      ) : (
        <table className="report-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Company</th>
              <th style={{ textAlign: 'left' }}>Product Name</th>
              <th style={{ textAlign: 'left' }}>Size</th>
              <th style={{ textAlign: 'right' }}>Purchase</th>
              <th style={{ textAlign: 'right' }}>Adj +</th>
              <th style={{ textAlign: 'right' }}>Adj -</th>
              <th style={{ textAlign: 'right' }}>Sale Return</th>
              <th style={{ textAlign: 'right' }}>Sale</th>
              <th style={{ textAlign: 'right' }}>Remaining</th>
            </tr>
          </thead>
          <tbody>
            {paginatedRows.map((r) => {
              const key = r._id || `${r.companyName || ''}-${r.modelNo || ''}-${r.size || ''}`;
              return (
                <tr key={key}>
                  <td>{r.companyName || '-'}</td>
                  <td>{r.modelNo || '-'}</td>
                  <td>{r.size || '-'}</td>
                  <td style={{ textAlign: 'right' }}>{getPurchaseQty(r)}</td>
                  <td style={{ textAlign: 'right' }}>{Number(r.adjustIncQty || 0)}</td>
                  <td style={{ textAlign: 'right' }}>{Number(r.adjustDecQty || 0)}</td>
                  <td style={{ textAlign: 'right' }}>{Number(r.saleReturnQty || 0)}</td>
                  <td style={{ textAlign: 'right' }}>{getSaleQtyForDisplay(r)}</td>
                  <td style={{ textAlign: 'right' }}>{getRemainingQty(r)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {!loading && sortedRows.length > itemsPerPage && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12 }}>
          <div>
            <button onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}>Prev</button>
            <span style={{ margin: '0 8px' }}>Page {currentPage} of {totalPages}</span>
            <button onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>Next</button>
          </div>
          <div>
            <label style={{ marginRight: 6 }}>Show</label>
            <select value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span style={{ marginLeft: 6 }}>per page</span>
          </div>
        </div>
      )}

      {error && <p className="report-message">{error}</p>}
    </div>
  );
};

export default StockReport;

